// components/common/Stepper.jsx
import React, {
  useState,
  useRef,
  useLayoutEffect,
  Children,
  forwardRef,
  useImperativeHandle,
} from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Stepper
 * - forwardRef to expose reset()
 * - Bottom navigation (Back / Next)
 * - Validates required fields inside the active step (data-step)
 */

const stepVariants = {
  enter: (dir) => ({ x: dir >= 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: "0%", opacity: 1 },
  exit: (dir) => ({ x: dir >= 0 ? "-50%" : "50%", opacity: 0 }),
};

const StepContentWrapper = ({
  isCompleted,
  currentStep,
  direction,
  children,
}) => {
  const [parentHeight, setParentHeight] = useState("auto");
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (ref.current) {
      const h = ref.current.offsetHeight;
      if (h && h > 0) setParentHeight(h);
    }
  }, [currentStep, children]);

  return <div className="w-full">{children}</div>;
};

const Stepper = forwardRef(
  (
    {
      children,
      initialStep = 1,
      backButtonText = "Back",
      nextButtonText = "Next",
      className = "",
    },
    ref
  ) => {
    const stepsArray = Children.toArray(children);
    const totalSteps = stepsArray.length;
    const [currentStep, setCurrentStep] = useState(initialStep);
    const [direction, setDirection] = useState(0);

    // Expose reset method
    useImperativeHandle(ref, () => ({
      reset: () => {
        setCurrentStep(initialStep);
        setDirection(0);
      },
      goTo: (step) => {
        if (step >= 1 && step <= totalSteps) {
          setDirection(step > currentStep ? 1 : -1);
          setCurrentStep(step);
        }
      },
      getStep: () => currentStep,
    }));

    const isCompleted = currentStep > totalSteps;
    const isLastStep = currentStep === totalSteps;

    const updateStep = (newStep, dir = 0) => {
      setDirection(dir);
      setCurrentStep(newStep);
    };

    const handleBack = () => {
      if (currentStep > 1) {
        updateStep(currentStep - 1, -1);
      }
    };

    const handleNext = () => {
      // Validate required fields in the visible step
      const activeContainer = document.querySelector(
        `[data-step='${currentStep}']`
      );
      if (activeContainer) {
        const requiredFields = activeContainer.querySelectorAll("[required]");
        for (const f of requiredFields) {
          // if checkbox/radio required, check for checked state or value
          if (f.type === "file") {
            // file inputs: accept if files present
            if (!f.files || f.files.length === 0) {
              f.focus();
              flashInvalid(f);
              return;
            }
          } else if (f.type === "checkbox" || f.type === "radio") {
            // let native validation handle radios/checkboxes (assuming grouped)
            if (!f.checked && f.required) {
              f.focus();
              flashInvalid(f);
              return;
            }
          } else {
            if (!String(f.value).trim()) {
              f.focus();
              flashInvalid(f);
              return;
            }
          }
        }
      }

      if (!isLastStep) {
        updateStep(currentStep + 1, 1);
      }
    };

    const flashInvalid = (el) => {
      el.classList.add("ring-2", "ring-red-400");
      setTimeout(() => el.classList.remove("ring-2", "ring-red-400"), 900);
    };

    return (
      <div className={`w-full ${className}`}>
        {/* Indicators */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {stepsArray.map((_, idx) => {
            const stepNum = idx + 1;
            const isActive = stepNum === currentStep;
            const isComplete = currentStep > stepNum;
            return (
              <React.Fragment key={stepNum}>
                <button
                  type="button"
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-semibold transition cursor-default ${
                    isActive
                      ? "bg-green-600 text-white"
                      : isComplete
                        ? "bg-green-200 text-green-700"
                        : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {isComplete ? "✓" : stepNum}
                </button>
                {idx < stepsArray.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 ${isComplete ? "bg-green-600" : "bg-gray-300"}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Content */}
        <StepContentWrapper
          isCompleted={isCompleted}
          currentStep={currentStep}
          direction={direction}
        >
          {stepsArray.map((step, i) =>
            i + 1 === currentStep ? (
              <div key={i} data-step={currentStep} className="px-0 pt-0 pb-6">
                {step}
              </div>
            ) : null
          )}
        </StepContentWrapper>

        {/* Navigation */}
        {!isCompleted && (
          <div className="flex justify-between items-center mt-4">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition"
              >
                {backButtonText}
              </button>
            ) : (
              <div />
            )}

            <div>
              {!isLastStep && (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition"
                >
                  {nextButtonText}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export const Step = ({ children }) => <div className="w-full">{children}</div>;

export default Stepper;
